import { EarningsInsight } from '@toggle/toggle';
import React from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { useMarkEventRead } from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import { EarningsInsightTooltip } from '../earnings-insight-tooltip/EarningsInsightTooltip';
import * as S from './EarningsInsightIcon.styles';
import { getEarningsInsightIconName } from './utils';

export interface EarningsInsightIconProps {
  size?: number;
  width?: number;
  height?: number;
  entityTag: string;
  earningsInsight: EarningsInsight;
  eventId?: string;
  read?: boolean;
  isDisabled?: boolean;
  enableHover?: boolean;
  isBordered?: boolean;
  source: PosthogTrackingSource;
  customSize?: { width: number; height: number };
}

export const EarningsInsightIcon = ({
  entityTag,
  earningsInsight,
  eventId = '',
  read = false,
  isDisabled = false,
  enableHover = true,
  isBordered = true,
  customSize,
  size,
  source,
  ...rest
}: EarningsInsightIconProps) => {
  const { openDrawer } = useEarningsInsightDrawer();
  const { mutate: markEventRead } = useMarkEventRead();

  const isPreview = earningsInsight.type === 'preview';
  const iconProps = {
    iconName: getEarningsInsightIconName(isPreview, isBordered),
    viewBox: isBordered ? '0 0 30 32' : '0 0 12 12',
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (eventId) {
      markEventRead(eventId);
    }
    openDrawer({
      entityTag,
      earningsId: earningsInsight.earnings_id,
      subtab: isPreview ? SubTabsType.Preview : SubTabsType.Recap,
      source,
    });
  };

  return (
    <TooltipContainer
      label={
        <EarningsInsightTooltip
          earningsInsight={earningsInsight}
          isPreview={isPreview}
          iconProps={iconProps}
          read={read}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!enableHover || isDisabled}
      inPortal
    >
      <S.StyledIcon
        {...iconProps}
        {...rest}
        {...(customSize
          ? { width: customSize.width, height: customSize.height }
          : { size })}
        $isDisabled={isDisabled}
        $isRead={read}
        $isBordered={isBordered}
        $enableHover={enableHover}
        onClick={handleClick}
      />
    </TooltipContainer>
  );
};
